import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef
} from "react"
import type { ReactNode } from "react"
import { createPortal } from "react-dom"
import useRerenderOnce from "../use-rerender-once"

const canUseDOM = () => typeof window !== "undefined" && window.document

export const StackingContext = createContext<HTMLElement | null>(null)

type PortalNodeProps = {
  children?: ReactNode
  className?: string
}

export function PortalNode({ children, className }: PortalNodeProps) {
  useRerenderOnce()
  const node = useRef<HTMLDivElement>(null)

  return (
    <StackingContext.Provider value={node.current}>
      <div className={className} ref={node}>
        {" "}
        {children}
      </div>
    </StackingContext.Provider>
  )
}

// Each Portal creates a new StackingContext, currently with no way to opt-out
// of this behavior. This means that anything rendered inside a nested portal
// will use the root of the previous portal.
function useRootNode() {
  return useContext(StackingContext) || document.body
}

function DOMPortal({ children }: { children?: ReactNode }) {
  const rootNode = useRootNode()
  const [node] = useState(() => document.createElement("div"))

  useEffect(() => {
    rootNode.appendChild(node)

    return () => {
      node.remove()
    }
  }, [rootNode, node])

  return (
    <StackingContext.Provider value={node}>
      {createPortal(children, node)}
    </StackingContext.Provider>
  )
}

function ServerPortal() {
  return null
}

function Portal({ children }: { children?: ReactNode }) {
  return canUseDOM() ? <DOMPortal>{children}</DOMPortal> : <ServerPortal />
}

export default Portal
